import type { OnboardingProfile } from '../api/types';

/**
 * Profile completion drives the "finish your profile" nudge modal.
 * Each onboarding path has its own set of fields; completion is the share
 * of those fields the org has filled in. The modal shows after registration
 * and on every login until completion reaches COMPLETION_THRESHOLD_PCT.
 */

export const COMPLETION_THRESHOLD_PCT = 80;

interface CompletionField {
  key: string;
  label: string;
  filled: (p: OnboardingProfile) => boolean;
}

const str = (v: string | null | undefined) => (v ?? '').trim().length > 0;
const arr = (v: string[] | null | undefined) => (v ?? []).length > 0;

const NONPROFIT_FIELDS: CompletionField[] = [
  { key: 'orgName', label: 'Organization name', filled: (p) => str(p.orgName) },
  { key: 'missionStatement', label: 'Mission statement', filled: (p) => str(p.missionStatement) },
  { key: 'taxExemptStatus', label: '501(c)(3) status', filled: (p) => p.taxExemptStatus != null },
  { key: 'foundingYear', label: 'Year founded', filled: (p) => p.foundingYear != null },
  { key: 'annualBudget', label: 'Annual budget', filled: (p) => str(p.annualBudget) },
  { key: 'focusAreas', label: 'Focus areas', filled: (p) => arr(p.focusAreas) },
  { key: 'serviceArea', label: 'Service area', filled: (p) => str(p.serviceArea) },
  { key: 'ein', label: 'EIN', filled: (p) => str(p.ein) },
  { key: 'uei', label: 'UEI', filled: (p) => str(p.uei) },
  { key: 'naicsPrimary', label: 'Primary NAICS code', filled: (p) => str(p.naicsPrimary) },
  { key: 'stage', label: 'Current stage', filled: (p) => str(p.stage) },
  { key: 'primaryGoal', label: 'Primary goal', filled: (p) => str(p.primaryGoal) },
];

const SMALLBIZ_FIELDS: CompletionField[] = [
  { key: 'orgName', label: 'Business name', filled: (p) => str(p.orgName) },
  { key: 'entityType', label: 'Entity type', filled: (p) => p.entityType != null },
  { key: 'yearsInBusiness', label: 'Years in business', filled: (p) => p.yearsInBusiness != null },
  { key: 'annualRevenue', label: 'Annual revenue', filled: (p) => str(p.annualRevenue) },
  { key: 'employeeCount', label: 'Employee count', filled: (p) => str(p.employeeCount) },
  { key: 'certifications', label: 'Certifications', filled: (p) => arr(p.certifications) },
  { key: 'samStatus', label: 'SAM.gov registration', filled: (p) => p.samStatus != null },
  { key: 'ein', label: 'EIN', filled: (p) => str(p.ein) },
  { key: 'uei', label: 'UEI', filled: (p) => str(p.uei) },
  { key: 'naicsPrimary', label: 'Primary NAICS code', filled: (p) => str(p.naicsPrimary) },
  { key: 'pscCodes', label: 'PSC codes', filled: (p) => arr(p.pscCodes) },
  { key: 'stage', label: 'Current stage', filled: (p) => str(p.stage) },
  { key: 'primaryGoal', label: 'Primary goal', filled: (p) => str(p.primaryGoal) },
];

export interface ProfileCompletion {
  pct: number;
  filled: number;
  total: number;
  missing: string[];
  complete: boolean;
}

/**
 * Fields both sectors share. R2-BR-D18: a workspace whose onboarding
 * path is not yet known (no profile) must NOT be shown the nonprofit
 * gap list — pre-fix a fresh FOR-PROFIT workspace was told "501(c)(3)
 * status" was missing, an artifact that only applies to nonprofit
 * workspaces (sector model: packages/api/src/lib/orgSector.ts /
 * quillGrounding). Sector-specific gaps (501(c)(3) status for
 * nonprofits; entity type / revenue for small businesses) appear only
 * once the workspace's own path says which sector it is.
 */
const COMMON_FIELDS: CompletionField[] = [
  { key: 'orgName', label: 'Organization name', filled: (p) => str(p.orgName) },
  { key: 'ein', label: 'EIN', filled: (p) => str(p.ein) },
  { key: 'uei', label: 'UEI', filled: (p) => str(p.uei) },
  { key: 'naicsPrimary', label: 'Primary NAICS code', filled: (p) => str(p.naicsPrimary) },
  { key: 'stage', label: 'Current stage', filled: (p) => str(p.stage) },
  { key: 'primaryGoal', label: 'Primary goal', filled: (p) => str(p.primaryGoal) },
];

export function profileCompletion(profile: OnboardingProfile | null): ProfileCompletion {
  if (!profile) {
    return {
      pct: 0,
      filled: 0,
      total: COMMON_FIELDS.length,
      missing: COMMON_FIELDS.map((f) => f.label),
      complete: false,
    };
  }
  const fields = profile.path === 'SMALLBIZ' ? SMALLBIZ_FIELDS : NONPROFIT_FIELDS;
  const missing = fields.filter((f) => !f.filled(profile)).map((f) => f.label);
  const filled = fields.length - missing.length;
  const pct = Math.round((filled / fields.length) * 100);
  return { pct, filled, total: fields.length, missing, complete: pct >= COMPLETION_THRESHOLD_PCT };
}
